import React, { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  ShoppingCart,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
} from "lucide-react"
import { useWebsiteProduct } from "@/hooks/useWebsiteProduct"
import { useSimilarProducts } from "@/hooks/useSimilarProducts"
import { useCart } from "@/hooks/useCart"
import { toast } from "sonner"
import { formatToDevanagariStyle } from "@/utilities/helper"

// Fallback placeholder image
const placeholderImage = "https://via.placeholder.com/800x800?text=No+Image"

const getProductImages = (product: any) => {
  // Use product images if available, otherwise use placeholder
  if (product?.images && product.images.length > 0) {
    return product.images.map((img: any) => img.url)
  }
  // If no images, use thumbnail if available
  if (product?.thumbnail?.url) {
    return [product.thumbnail.url]
  }
  return [placeholderImage]
}

const getProductSingleImage = (product: any) => {
  if (product?.thumbnail?.url) return product.thumbnail.url
  if (product?.images && product.images.length > 0) return product.images[0].url
  return placeholderImage
}

export const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const productId = id ? parseInt(id, 10) : 0
  
  const { data: productData, isLoading } = useWebsiteProduct(productId)
  const { data: similarProductsData, isLoading: isSimilarLoading } = useSimilarProducts(productId)

  const { addItem, isAdding } = useCart()
  const [selectedImageIndex, setSelectedImageIndex] = useState(0)
  const [quantity, setQuantity] = useState(1)

  const product = (productData as any)?.data?.data || (productData as any)?.data
  const similarProducts = (similarProductsData as any)?.data?.data || (similarProductsData as any)?.data || []
  
  const token = localStorage.getItem("website_token")

  const images = product ? getProductImages(product) : []

  // Reset selected image when product changes
  useEffect(() => {
    setSelectedImageIndex(0)
    setQuantity(1)
    window.scrollTo(0, 0)
  }, [productId])

  const handleAddToCart = () => {
    if (!token) {
      toast.error("Please login to add items to cart")
      navigate("/website/login")
      return
    }

    if (!product) return

    addItem({
      product_id: product.id,
      quantity: quantity,
    })
    setQuantity(1) // Reset quantity after adding
  }

  const handlePreviousImage = () => {
    setSelectedImageIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1))
  }

  const handleNextImage = () => {
    setSelectedImageIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1))
  }

  if (isLoading) {
    return (
      <div className="bg-gray-50 min-h-screen py-10 flex items-center justify-center">
        <div className="text-gray-500">Loading product...</div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="bg-gray-50 min-h-screen py-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={() => navigate("/website")}
            className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft size={20} className="mr-2" />
            Back to Products
          </button>
          <div className="bg-white rounded-lg shadow-sm p-12 text-center">
            <p className="text-gray-500 text-lg">Product not found</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-gray-50 min-h-screen py-10">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <button
          onClick={() => navigate("/website")}
          className="mb-6 flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Products
        </button>

        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-12">
          <div className="lg:grid lg:grid-cols-7 lg:gap-x-8">
            {/* Image Gallery */}
            <div className="lg:col-span-3">
              <div className="aspect-square w-full overflow-hidden rounded-lg bg-gray-100 relative group">
                <img
                  src={images[selectedImageIndex] || placeholderImage}
                  alt={product.name}
                  className="h-full w-full object-cover object-center"
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage
                  }}
                />
                {/* Navigation arrows for desktop */}
                {images.length > 1 && (
                  <>
                    <button
                      onClick={handlePreviousImage}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Previous image"
                    >
                      <ChevronLeft size={24} className="text-gray-900" />
                    </button>
                    <button
                      onClick={handleNextImage}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity"
                      aria-label="Next image"
                    >
                      <ChevronRight size={24} className="text-gray-900" />
                    </button>
                  </>
                )}
              </div>

              {/* Thumbnail Gallery */}
              {images.length > 1 && (
                <div className="mt-4 grid grid-cols-3 gap-4">
                  {images.map((image: string, index: number) => (
                    <button
                      key={index}
                      onClick={() => setSelectedImageIndex(index)}
                      className={`aspect-square overflow-hidden rounded-lg border-2 ${
                        selectedImageIndex === index
                          ? "border-indigo-600"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      <img
                        src={image || placeholderImage}
                        alt={`${product.name} view ${index + 1}`}
                        className="h-full w-full object-cover object-center"
                        onError={(e) => {
                          e.currentTarget.src = placeholderImage
                        }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Product Info */}
            <div className="lg:col-span-4 px-6 py-8 lg:px-8 lg:py-10">
              <div>
                <h1 className="text-3xl font-bold text-gray-900">
                  {product.name}
                </h1>
                <p className="mt-2 text-2xl font-medium text-gray-900">
                  Rs. {formatToDevanagariStyle(product.sell_price) || "0.00"}
                </p>
              </div>

              <div className="mt-6">
                {product.stock > 0 ? (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    In Stock ({product.stock} available)
                  </span>
                ) : (
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                    Out of Stock
                  </span>
                )}
              </div>

              {product.sku && (
                <div className="mt-4">
                  <p className="text-sm text-gray-500">
                    SKU: <span className="text-gray-900">{product.sku}</span>
                  </p>
                </div>
              )}

              {/* Tags */}
              {product.tags && product.tags.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-medium text-gray-900">Tags</h3>
                  <div className="mt-2 flex flex-wrap gap-2">
                    {product.tags.map((tag: any) => (
                      <span
                        key={tag.id}
                        className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity and Add to Cart */}
              <div className="mt-8">
                <div className="flex items-center space-x-4 mb-4">
                  <label
                    htmlFor="quantity"
                    className="text-sm font-medium text-gray-700"
                  >
                    Quantity:
                  </label>
                  <div className="flex items-center border rounded-md">
                    <button
                      onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                      className="p-2 hover:bg-gray-100"
                      disabled={quantity <= 1}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    <span className="px-4 py-2 min-w-[60px] text-center">
                      {quantity}
                    </span>
                    <button
                      onClick={() =>
                        setQuantity((q) => Math.min(product.stock || 1, q + 1))
                      }
                      className="p-2 hover:bg-gray-100"
                      disabled={quantity >= (product.stock || 0)}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                </div>

                <button
                  onClick={handleAddToCart}
                  disabled={isAdding || product.stock === 0}
                  className="w-full bg-indigo-600 border border-transparent rounded-md shadow-sm py-3 px-4 text-base font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  <ShoppingCart size={20} />
                  {isAdding ? "Adding to Cart..." : "Add to Cart"}
                </button>
              </div>

              {/* Description section - if available in future */}
              {product.description && (
                <div className="mt-8 border-t border-gray-200 pt-8">
                  <h3 className="text-sm font-medium text-gray-900">
                    Description
                  </h3>
                  <p className="mt-4 text-sm text-gray-600">
                    {product.description}
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Similar Products */}
        {!isSimilarLoading && similarProducts.length > 0 && (
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Similar Products</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {similarProducts.map((p: any) => (
                <div
                  key={p.id}
                  className="group relative cursor-pointer bg-white rounded-lg p-4 shadow-sm transition-shadow"
                  onClick={() => navigate(`/website/product/${p.id}`)}
                >
                  <div className="h-[230px] aspect-w-1 aspect-h-1 w-full overflow-hidden rounded-md bg-gray-200 xl:aspect-w-7 xl:aspect-h-8">
                    <img
                      src={getProductSingleImage(p)}
                      alt={p.name}
                      className="h-full w-full object-cover object-center group-hover:scale-105 transition-transform duration-300"
                      onError={(e) => {
                        e.currentTarget.src = placeholderImage
                      }}
                    />
                  </div>
                  <h3 className="mt-4 text-sm text-gray-700 hover:text-indigo-600 line-clamp-2">
                    {p.name}
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <p className="text-lg font-medium text-gray-900">
                      Rs. {formatToDevanagariStyle(p.sell_price) || "0.00"}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
